import { createContext, useContext, useState } from 'react';
import { useModal } from 'framework/components/ui/Modal';
import { useSelector } from 'react-redux';
import { modalSelectors } from 'framework/selectors';
import { addProgressBar } from 'framework/components/ui/ProgressBar/useProgressBar';
import {
  FILEMANAGER_MODAL_ADD_FOLDER,
  FILEMANAGER_MODAL_MOVE_TO_FOLDER,
  FILEMANAGER_DELETE_CONFIRM_MODAL,
} from './constants';
import { FileFolder, FileFolderType } from './types';
import { fetchFolders, formatChildren } from './services';

type Store = {
  loadingReference?: string;
  initializing: boolean;
  data: FileFolder[];
  dataCopy: FileFolder[];
  rowClick: (row: FileFolder, includeFiles?: boolean) => Promise<boolean>;
  fetchSubFolders: (row: FileFolder, includeFiles: boolean) => Promise<void>;
  searchTerm?: string;
  searchFiles: (searchTerm: string) => Promise<void>;
};

export type DeleteFunc = () => Promise<void> | void;

export const MAX_SELECTED_FILES = 50;

// Exported pure functions for testing
export const isFileInSelectedList = (file: FileFolder, selectedFiles: FileFolder[]): boolean =>
  selectedFiles.some((f) => f.reference === file.reference);

export const canSelectMoreFiles = (selectedFiles: FileFolder[]): boolean => selectedFiles.length < MAX_SELECTED_FILES;

export const toggleFileInSelection = (file: FileFolder, selectedFiles: FileFolder[]): FileFolder[] => {
  const isSelected = isFileInSelectedList(file, selectedFiles);
  if (isSelected) {
    return selectedFiles.filter((f) => f.reference !== file.reference);
  }
  if (selectedFiles.length >= MAX_SELECTED_FILES) {
    return selectedFiles;
  }
  return [...selectedFiles, file];
};

export const hasStore = (store?: any): boolean => !!store;
export const shouldRefreshActiveFolder = (activeFolder?: FileFolder): boolean => !!activeFolder;
export const hasBulkAction = (bulkAction: string): boolean => !!bulkAction;

export const useFileManagerContextInternal = (confirmationModalName?: string) => {
  const [previewableFile, setPreviewableFile] = useState<FileFolder>();
  const [activeFolder, setActiveFolder] = useState<FileFolder>();
  const [forRoot, setForRoot] = useState<boolean>();
  const [store, setStore] = useState<Store>();
  const [deleteMode, setDeleteMode] = useState<FileFolderType>();
  const [onDeleteFunc, setOnDeleteFunc] = useState<DeleteFunc>();
  const [selectedFiles, setSelectedFiles] = useState<FileFolder[]>([]);
  const [bulkAction, setBulkAction] = useState<string>('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const { openModal: openFolderModal } = useModal(FILEMANAGER_MODAL_ADD_FOLDER);
  const { openModal: openDeleteConfirmModal } = useModal(confirmationModalName || FILEMANAGER_DELETE_CONFIRM_MODAL);

  const handleOpenFolderModal = (root?: boolean) => () => {
    setForRoot(root);
    openFolderModal();
  };

  const rootFolderModalOpen = useSelector((state) => modalSelectors.isModalOpen(state, FILEMANAGER_MODAL_ADD_FOLDER));

  const moveFileModalOpen = useSelector((state) => modalSelectors.isModalOpen(state, FILEMANAGER_MODAL_MOVE_TO_FOLDER));

  const prompDelete = (mode: FileFolderType, onDelete: DeleteFunc) => () => {
    setDeleteMode(mode);
    setOnDeleteFunc(() => onDelete);
    openDeleteConfirmModal();
  };

  const onFolderCreated = addProgressBar(async () => {
    store.initializing = true;
    await fetchFolders('', true)
      .then((res) => {
        const formattedChildren = formatChildren(res.data ?? []);
        store.dataCopy = formattedChildren;

        // If there is a searchTerm while folder is created, perform the search again
        if (store.searchTerm?.length) {
          store.searchFiles(store.searchTerm);
          return;
        }

        store.data = formattedChildren;
      })
      .finally(() => {
        store.initializing = false;
      });
  });

  const onSubFolderCreated = addProgressBar(async () => {
    store.fetchSubFolders(activeFolder, true);
  });

  const onFileMoved = addProgressBar(async () => {
    setPreviewableFile(undefined);
    setSelectedFiles([]);
    setBulkAction('');

    // Ensure store exists and refresh the current folder view
    if (!hasStore(store)) {
      return;
    }

    if (shouldRefreshActiveFolder(activeFolder)) {
      await store.fetchSubFolders(activeFolder, true);
    } else {
      // If no active folder, refresh the root
      store.initializing = true;
      await fetchFolders('', true)
        .then((res) => {
          store.data = formatChildren(res.data ?? []);
        })
        .finally(() => {
          store.initializing = false;
        });
    }
  });

  const toggleFileSelection = (file: FileFolder) => {
    setSelectedFiles((prev) => toggleFileInSelection(file, prev));
  };

  const clearSelectedFiles = () => {
    setSelectedFiles([]);
    setBulkAction('');
  };

  const isFileSelected = (file: FileFolder) => isFileInSelectedList(file, selectedFiles);

  const canSelectMore = canSelectMoreFiles(selectedFiles);

  return {
    store,
    previewableFile,
    rootFolderModalOpen,
    moveFileModalOpen,
    activeFolder,
    forRoot,
    deleteMode,
    onDeleteFunc,
    selectedFiles,
    bulkAction,
    refreshTrigger,
    setStore,
    setPreviewableFile,
    handleOpenFolderModal,
    onFolderCreated,
    setActiveFolder,
    onFileMoved,
    prompDelete,
    onSubFolderCreated,
    toggleFileSelection,
    clearSelectedFiles,
    isFileSelected,
    canSelectMore,
    setBulkAction,
    setRefreshTrigger,
  };
};

type FileManagerContextInternal = ReturnType<typeof useFileManagerContextInternal>;
export const FileManagerContext = createContext<Partial<FileManagerContextInternal>>({} as any);

export const useFileManagerContext = () => useContext(FileManagerContext);
